/**
 * Admin — Allowed Signups
 * Manage which email addresses are permitted to create new organisations.
 */

import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { createFileRoute } from '@tanstack/react-router';
import { formatDateNumeric } from '@/lib/dates';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export const Route = createFileRoute('/admin/allowed-signups')({
  component: AllowedSignupsPage,
});

// ============================================
// API helpers
// ============================================

const BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/bss-organizations`;

async function getToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return session.access_token;
}

interface AllowedSignupRow {
  id: string;
  email: string;
  note: string | null;
  created_at: string;
  used_at: string | null;
}

async function fetchAllowedSignups(): Promise<AllowedSignupRow[]> {
  const token = await getToken();
  const res = await fetch(`${BASE}/allowed-signups`, {
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to fetch allowed signups');
  }
  const data = await res.json();
  return data.allowedSignups as AllowedSignupRow[];
}

async function addAllowedSignup(email: string, note?: string): Promise<AllowedSignupRow> {
  const token = await getToken();
  const res = await fetch(`${BASE}/allowed-signups`, {
    method: 'POST',
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, note }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to add email');
  }
  const data = await res.json();
  return data.allowedSignup as AllowedSignupRow;
}

async function removeAllowedSignup(email: string): Promise<void> {
  const token = await getToken();
  const res = await fetch(`${BASE}/allowed-signups`, {
    method: 'DELETE',
    headers: { ...EDGE_FUNCTION_HEADERS, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || 'Failed to remove email');
  }
}

// ============================================
// Page component
// ============================================

function AllowedSignupsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ['allowed-signups'],
    queryFn: fetchAllowedSignups,
  });

  const addMutation = useMutation({
    mutationFn: ({ email, note }: { email: string; note?: string }) =>
      addAllowedSignup(email, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowed-signups'] });
      toast({ title: 'Added', description: 'Email added to allowlist.' });
      setAddOpen(false);
      setNewEmail('');
      setNewNote('');
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (email: string) => removeAllowedSignup(email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allowed-signups'] });
      toast({ title: 'Removed', description: 'Email removed from allowlist.' });
    },
    onError: (err: Error) => {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    },
  });

  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');

  const handleAdd = () => {
    if (!newEmail.trim()) return;
    addMutation.mutate({ email: newEmail.trim(), note: newNote.trim() || undefined });
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Allowed Signups</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Only email addresses listed here can create new organisations during onboarding.
          </p>
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Email
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add to Allowlist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label htmlFor="new-email">Email address</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="user@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  autoFocus
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-note">Note (optional)</Label>
                <Input
                  id="new-note"
                  placeholder="e.g. Client name, referred by..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={addMutation.isPending || !newEmail.trim()}>
                {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load allowlist'}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows && rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No emails on the allowlist yet. Add one above.
        </div>
      ) : (
        <div className="border border-border rounded-lg divide-y divide-border">
          {rows?.map((row) => (
            <div key={row.id} className="flex items-center justify-between px-4 py-3">
              <div className="space-y-0.5 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{row.email}</span>
                  {row.used_at && (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      Used
                    </Badge>
                  )}
                </div>
                {row.note && (
                  <p className="text-xs text-muted-foreground truncate">{row.note}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Added {formatDateNumeric(row.created_at)}
                  {row.used_at && ` · Used ${formatDateNumeric(row.used_at)}`}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive shrink-0 ml-4"
                onClick={() => removeMutation.mutate(row.email)}
                disabled={removeMutation.isPending}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
